import React, { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * AuthGate — gates the entire app on a Microsoft SSO session.
 *
 * Behaviour:
 *   • On mount, calls GET /api/auth/me. If it returns 200, renders `children`.
 *   • If it returns 401, renders a sign-in card that redirects to the
 *     backend's /api/auth/microsoft/login (which itself redirects to
 *     Microsoft's tenant-restricted authorize endpoint).
 *   • Any other error surfaces as a plain error card (Auth failure /
 *     backend down / not-allow-listed after Microsoft auth returns).
 *
 * There is intentionally no client-side "authentication" here — the
 * session cookie is HTTP-only and set by the backend on the /callback
 * hop. This component just asks the backend "who am I?" and gates the
 * rest of the tree accordingly.
 */
export function AuthGate({ children }) {
  const [state, setState] = useState({ loading: true, user: null, error: null });

  // Desktop kit ships with REACT_APP_LOCAL_MODE=1 baked into the build,
  // so /api/auth is never mounted locally and we render `children` bypassing
  // the SSO gate. The cloud build must never carry this flag.
  const LOCAL_MODE = process.env.REACT_APP_LOCAL_MODE === "1";

  useEffect(() => {
    if (LOCAL_MODE) {
      setState({ loading: false, user: { email: "local", localMode: true }, error: null });
      return () => {};
    }
    let cancelled = false;
    api.me()
      .then((u) => { if (!cancelled) setState({ loading: false, user: u, error: null }); })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 401) {
          setState({ loading: false, user: null, error: null });
        } else {
          setState({
            loading: false, user: null,
            error: e?.response?.data?.detail || e.message || "Auth check failed.",
          });
        }
      });
    return () => { cancelled = true; };
  }, [LOCAL_MODE]);

  if (state.loading) {
    return (
      <div data-testid="auth-gate-loading"
           style={{ padding: 40, fontFamily: "system-ui" }}>
        Checking sign-in…
      </div>
    );
  }

  if (state.user) {
    if (state.user.localMode) {
      return <>{children}</>;
    }
    return (
      <>
        <UserBadge email={state.user.email} />
        {children}
      </>
    );
  }

  // Not authenticated → sign-in card
  return (
    <div data-testid="auth-gate-signin"
         style={{
           minHeight: "100vh", display: "grid", placeItems: "center",
           background: "#0f1419", color: "#e5e7eb",
           fontFamily: "system-ui, -apple-system, sans-serif",
         }}>
      <div style={{
        width: "min(440px, 92vw)", padding: "32px 34px",
        background: "#1a2130", border: "1px solid #2a3244",
        borderRadius: 10,
        boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
      }}>
        <div style={{ fontSize: 12, letterSpacing: 2,
                      color: "#7f8fa6", marginBottom: 4 }}>
          MONTH-END COMMANDER
        </div>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700 }}>
          Reconciliation Console
        </h1>
        <p style={{ marginTop: 12, marginBottom: 22, fontSize: 13,
                    color: "#a0aab8", lineHeight: 1.5 }}>
          Sign in with your Microsoft 365 account to access the close.
          Only authorised Controllers on the <code>coolmanfuels.ca</code>
          tenant can proceed.
        </p>

        {state.error && (
          <div data-testid="auth-gate-error"
               style={{
                 padding: "8px 10px", marginBottom: 14, borderRadius: 6,
                 background: "#5c1e1e", color: "#ffd7d7",
                 fontSize: 12, lineHeight: 1.5,
               }}>
            {state.error}
          </div>
        )}

        <a href={api.loginUrl()}
           data-testid="auth-gate-signin-btn"
           style={{
             display: "block", textAlign: "center",
             padding: "11px 18px", borderRadius: 6, border: "none",
             background: "#2c62db", color: "#fff", fontSize: 14,
             fontWeight: 600, cursor: "pointer", textDecoration: "none",
           }}>
          Sign in with Microsoft
        </a>
        <div style={{ fontSize: 11, color: "#6b7684", marginTop: 14,
                      textAlign: "center", lineHeight: 1.5 }}>
          Access is limited to the <code>coolmanfuels.ca</code> tenant and
          an explicit user allow-list. Every sign-in and rejection is
          audit-logged.
        </div>
      </div>
    </div>
  );
}

function UserBadge({ email }) {
  const [busy, setBusy] = useState(false);
  const doLogout = async () => {
    setBusy(true);
    try { await api.logout(); } catch (_) {}
    window.location.reload();
  };
  return (
    <div data-testid="user-badge"
         style={{
           position: "fixed", top: 8, right: 8, zIndex: 100,
           display: "flex", alignItems: "center", gap: 8,
           padding: "4px 8px", borderRadius: 6,
           background: "rgba(20, 30, 46, 0.75)", color: "#dfe4ec",
           fontFamily: "system-ui", fontSize: 12,
           border: "1px solid rgba(255,255,255,0.06)",
           backdropFilter: "blur(6px)",
         }}>
      <span data-testid="user-badge-email">{email}</span>
      <button data-testid="user-badge-signout"
              onClick={doLogout} disabled={busy}
              style={{
                background: "transparent", color: "#8bb3ff", border: "none",
                cursor: "pointer", fontSize: 12, padding: 0,
              }}>
        {busy ? "…" : "Sign out"}
      </button>
    </div>
  );
}
